import React, { useEffect, useState, useCallback } from 'react';
import { formatDate } from './dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { url, authFetch, userMoveInDepositUrl } from './apiClient';
import { Link } from 'react-router-dom';
import { billTotal, monthLabel, rupees } from './dashboardStats';
import Icon from './Icons';
import './Dashboard.css';
import './TenantBills.css';

// How a deposit recorded by the owner was paid -> its translation key.
const DEPOSIT_METHOD_KEYS = { CASH: 'bills.methodCash', UPI: 'bills.methodUpi', BANK_TRANSFER: 'bills.methodBank' };

function TenantBills({ username, fullName }) {
  const { t, i18n } = useTranslation();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingBillId, setPayingBillId] = useState(null); // Track which bill is being paid
  const [moveInInfo, setMoveInInfo] = useState({ moveInDate: null, demandedDeposit: null, totalAmountDeposited: 0, history: [] });
  const [infoLoading, setInfoLoading] = useState(false);
  const [showDepositHistory, setShowDepositHistory] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [payingDeposit, setPayingDeposit] = useState(false);
  const navigate = useNavigate();

  const fetchDepositInfo = useCallback(async () => {
    if (!username) return;
    setInfoLoading(true);
    try {
      const res = await authFetch(userMoveInDepositUrl.self(username));
      if (res.ok) {
        const data = await res.json();
        if (!data.error) {
          setMoveInInfo({
            moveInDate: data.moveInDate || null,
            demandedDeposit: data.demandedDeposit != null ? Number(data.demandedDeposit) : null,
            totalAmountDeposited: Number(data.totalAmountDeposited || 0),
            history: Array.isArray(data.history) ? data.history : []
          });
        }
      }
    } catch (e) {
      console.warn('Move-in/deposit load failed', e);
    } finally {
      setInfoLoading(false);
    }
  }, [username]);

  const fetchBills = useCallback(async () => {
    if (!username) return;
    try {
  const response = await authFetch(url.tenantBills(username));
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      // Normalize miscellaneous field naming (miscellaneous | misc | balance | maintenance | otherCharges)
      const normalized = Array.isArray(data) ? data.map(b => ({
        ...b,
        miscellaneous: b.miscellaneous ?? b.misc ?? b.balance ?? b.maintenance ?? b.otherCharges ?? b.otherCharge ?? b.extra ?? 0
      })) : [];
      setBills(normalized);
    } catch (error) {
      console.error("Error fetching bills:", error);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    if (!username) {
      // If username missing, force back to login
      navigate('/login', { replace: true });
      return;
    }
    fetchBills();
    fetchDepositInfo();
  }, [username, fetchBills, fetchDepositInfo, navigate]);

  const payNow = async (bill) => {
    // Prevent duplicate payment triggers for the same bill
    if (payingBillId === bill.id) return;
    setPayingBillId(bill.id);

    // The order is created server-side (amount computed from the bill itself, not
    // trusted from the browser) so Razorpay's post-payment signature can later be
    // verified against a specific order/amount instead of just taking our word for it.
    let order;
    try {
      const res = await authFetch(url.createOrder(bill.id), { method: 'POST' });
      order = await res.json();
      if (!res.ok) throw new Error(order.error || `HTTP ${res.status}`);
    } catch (err) {
      alert(`Could not start payment: ${err.message}`);
      setPayingBillId(null);
      return;
    }

    const options = {
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.orderId,
      name: "Tenant Rent Billing",
      description: `Payment for ${bill.monthYear}`,
      handler: async (response) => {
        try {
          const res = await authFetch(url.markBillPaid(bill.id), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          // markPaid itself records the payment log (with which bill it paid).
          alert(`✅ ${t('bills.paymentSuccessful')}\n${t('bills.paymentId')}: ${response.razorpay_payment_id}`);
          fetchBills();
        } catch (err) {
          alert(`Payment could not be verified: ${err.message}`);
          console.error("Error after payment success:", err);
        } finally {
          setPayingBillId(null); // Reset after payment
        }
      },
      prefill: { name: username, email: '', contact: '' },
      theme: { color: "#7a4a2c" }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      alert(`❌ Payment failed: ${resp.error.description}`);
      authFetch(url.logPaymentFailure(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resp.error)
      });
      setPayingBillId(null); // Reset if failed
    });
    rzp.open();
  };

  // How much deposit can still be paid. null = the owner hasn't set a demanded
  // amount, so there's no limit to compare against. Rounded to paise so float
  // dust can't leave a fully paid deposit looking like it has 0.0000001 left.
  const depositRemaining = moveInInfo.demandedDeposit != null
    ? Math.max(Math.round((moveInInfo.demandedDeposit - moveInInfo.totalAmountDeposited) * 100) / 100, 0)
    : null;
  const depositComplete = depositRemaining !== null && depositRemaining <= 0;

  const payDeposit = async () => {
    const amount = Number(depositAmount);
    if (!amount || amount <= 0) {
      alert('Enter an amount greater than zero');
      return;
    }
    if (depositComplete) return;
    if (depositRemaining !== null && amount > depositRemaining) {
      alert(t('bills.depositTooMuch', { amount: depositRemaining }));
      return;
    }
    if (payingDeposit) return;
    setPayingDeposit(true);

    // Same pattern as bill payment: the order amount is decided server-side
    // from what we send here, then re-verified against Razorpay's own order
    // record after payment -- so the amount can't be tampered with in transit.
    let order;
    try {
      const res = await authFetch(userMoveInDepositUrl.createOrder(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      order = await res.json();
      if (!res.ok) throw new Error(order.error || `HTTP ${res.status}`);
    } catch (err) {
      alert(`Could not start payment: ${err.message}`);
      setPayingDeposit(false);
      return;
    }

    const options = {
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.orderId,
      name: "Tenant Rent Billing",
      description: "Security deposit payment",
      handler: async (response) => {
        try {
          const res = await authFetch(userMoveInDepositUrl.verify(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          alert(`✅ Deposit payment successful!\nPayment ID: ${response.razorpay_payment_id}`);
          setDepositAmount('');
          await fetchDepositInfo();
        } catch (err) {
          alert(`Payment could not be verified: ${err.message}`);
          console.error("Error after deposit payment success:", err);
        } finally {
          setPayingDeposit(false);
        }
      },
      prefill: { name: username, email: '', contact: '' },
      theme: { color: "#7a4a2c" }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      alert(`❌ Payment failed: ${resp.error.description}`);
      setPayingDeposit(false);
    });
    rzp.open();
  };

  if (loading) {
    return <p style={{ textAlign: 'center', marginTop: '40px' }}>Loading bills…</p>;
  }

  const unpaidBills = bills.filter((b) => !b.paid);
  const totalDue = unpaidBills.reduce((sum, b) => sum + billTotal(b), 0);
  // The hero's one-tap button pays the oldest bill first.
  const oldest = [...unpaidBills].sort((a, b) => String(a.monthYear).localeCompare(String(b.monthYear)))[0];
  const month = (m) => monthLabel(m, i18n.language);
  const depositPct = moveInInfo.demandedDeposit
    ? Math.min(100, Math.round((moveInInfo.totalAmountDeposited / moveInInfo.demandedDeposit) * 100))
    : null;

  return (
    <div className="tenant-home">
      <section className={`tenant-hero ${unpaidBills.length ? '' : 'tenant-hero--clear'}`}>
        <div className="tenant-hero__main">
          <div className="tenant-hero__hello">{t('tenantHome.greeting', { name: fullName || username })}</div>
          {unpaidBills.length ? (
            <>
              <div className="tenant-hero__label">{t('tenantHome.totalDue')}</div>
              <div className="tenant-hero__amount">{rupees(totalDue)}</div>
              <span className="tenant-hero__pill">{t('tenantHome.pendingCount', { count: unpaidBills.length })}</span>
            </>
          ) : (
            <>
              <div className="tenant-hero__amount tenant-hero__amount--clear"><Icon name="check" size={34} /> {t('tenantHome.allClear')}</div>
              <div className="tenant-hero__label">{t('tenantHome.allClearSub')}</div>
            </>
          )}
        </div>
        {oldest && (
          <div className="tenant-hero__cta">
            <button
              type="button"
              className="btn btn--white btn--lg"
              onClick={() => payNow(oldest)}
              disabled={payingBillId === oldest.id}
            >
              {payingBillId === oldest.id ? t('bills.processing') : t('tenantHome.payOldest', { month: month(oldest.monthYear) })}
              <Icon name="arrow" size={18} />
            </button>
            <div className="tenant-hero__secure"><Icon name="shield" size={14} /> {t('tenantHome.securePay')}</div>
          </div>
        )}
      </section>

      <div className="tenant-tiles">
        <div className="tile">
          <div className="tile__icon kpi__icon--brown"><Icon name="shield" /></div>
          <div className="tile__body">
            <div className="tile__label">{t('tenantHome.deposit')}</div>
            <div className="tile__value">
              {infoLoading ? '…' : rupees(moveInInfo.totalAmountDeposited)}
              {!infoLoading && moveInInfo.demandedDeposit != null && <span className="tile__of"> / {rupees(moveInInfo.demandedDeposit)}</span>}
            </div>
            {depositPct != null && <div className="bar"><div className="bar__fill bar__fill--brown" style={{ width: `${depositPct}%` }} /></div>}
          </div>
        </div>
        <div className="tile">
          <div className="tile__icon kpi__icon--blue"><Icon name="calendar" /></div>
          <div className="tile__body">
            <div className="tile__label">{t('bills.moveInDate')}</div>
            <div className="tile__value">{moveInInfo.moveInDate ? formatDate(moveInInfo.moveInDate) : '—'}</div>
          </div>
        </div>
        <Link to="/complaints" className="tile tile--link">
          <div className="tile__icon kpi__icon--amber"><Icon name="wrench" /></div>
          <div className="tile__body"><div className="tile__value tile__value--sm">{t('tenantHome.quickComplaint')}</div></div>
          <Icon name="arrow" size={16} className="tile__arrow" />
        </Link>
        <Link to="/history" className="tile tile--link">
          <div className="tile__icon kpi__icon--green"><Icon name="history" /></div>
          <div className="tile__body"><div className="tile__value tile__value--sm">{t('tenantHome.quickHistory')}</div></div>
          <Icon name="arrow" size={16} className="tile__arrow" />
        </Link>
      </div>

      <div className="bills-page">
        <h2 className="bills-heading">
          {t('bills.heading')}
        </h2>

        <div className="bills-summary">
          <div>
            <strong>{t('bills.depositPaid')}:</strong>{' '}
            {infoLoading ? '…' : `₹${moveInInfo.totalAmountDeposited}`}
            {!infoLoading && moveInInfo.demandedDeposit != null && (
              <span style={{ color: 'var(--muted)' }}> / ₹{moveInInfo.demandedDeposit} {t('bills.demanded')}</span>
            )}
          </div>
          {!infoLoading && moveInInfo.demandedDeposit != null && (
            <div>
              {moveInInfo.totalAmountDeposited >= moveInInfo.demandedDeposit ? (
                <span style={{ color: '#16a34a', fontWeight: 600 }}>✅ {t('bills.fullyPaid')}</span>
              ) : (
                <span style={{ color: '#dc2626', fontWeight: 600 }}>
                  ₹{(moveInInfo.demandedDeposit - moveInInfo.totalAmountDeposited).toFixed(2)} {t('bills.remaining')}
                </span>
              )}
            </div>
          )}
          {!depositComplete && (
            <div className="deposit-pay-row">
              <input
                type="number"
                min="1"
                step="0.01"
                max={depositRemaining !== null ? depositRemaining : undefined}
                placeholder={t('bills.amountPlaceholder')}
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="deposit-pay-input"
                disabled={payingDeposit || depositComplete}
              />
              <button
                className="deposit-pay-btn"
                onClick={payDeposit}
                disabled={payingDeposit || depositComplete || infoLoading}
              >
                {payingDeposit ? t('bills.processing') : t('bills.payDeposit')}
              </button>
            </div>
          )}
          {!infoLoading && depositRemaining !== null && depositRemaining > 0 && (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('bills.depositUpTo', { amount: depositRemaining })}</div>
          )}
          {moveInInfo.history.length > 0 && (
            <button
              type="button"
              className="deposit-history-toggle"
              onClick={() => setShowDepositHistory(v => !v)}
            >
              {showDepositHistory ? t('bills.hideDepositHistory') : t('bills.viewDepositHistory')} ({moveInInfo.history.length})
            </button>
          )}
          {showDepositHistory && moveInInfo.history.length > 0 && (
            <div className="deposit-history-list">
              {moveInInfo.history.map((entry) => (
                <div className="deposit-history-row" key={entry.id}>
                  <span className="deposit-history-date">
                    {entry.paidDate ? formatDate(entry.paidDate) : '—'}
                  </span>
                  <span className="deposit-history-source">
                    {entry.source === 'razorpay' ? t('bills.paidOnline') : t('bills.recordedByAdmin')}
                    {entry.method ? ` · ${t(DEPOSIT_METHOD_KEYS[entry.method] || '', entry.method)}` : ''}
                    {(entry.paymentId || entry.reference) ? ` · ${t('bills.refNo')} ${entry.paymentId || entry.reference}` : ''}
                    {entry.notes ? ` · ${entry.notes}` : ''}
                  </span>
                  <span className="deposit-history-amount">₹{entry.amount}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {unpaidBills.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--muted)' }}>
            {bills.length === 0 ? (
              <>{t('bills.noBillsAtAll')} <strong>{username}</strong>.</>
            ) : t('bills.noUnpaidBills')}
          </p>
        ) : (
          <div className="bill-list">
            {unpaidBills.map((bill, idx) => {
              const total = Number(bill.rent || 0) + Number(bill.water || 0) + Number(bill.electricity || 0) + Number(bill.miscellaneous || 0);
              const isElectricity = bill.billType === 'ELECTRICITY';
              return (
                <div className="bill-card" key={bill.id || idx}>
                  <div className="bill-card-header">
                    <span className="bill-month">
                      {month(bill.monthYear)}
                      <span className="bill-type-tag">{isElectricity ? t('bills.electricityTag') : t('bills.rentTag')}</span>
                    </span>
                    <button
                      className="bill-pay-btn"
                      onClick={() => payNow(bill)}
                      disabled={payingBillId === bill.id}
                    >
                      {payingBillId === bill.id ? t('bills.processing') : t('bills.pay')}
                    </button>
                  </div>
                  <div className="bill-breakdown">
                    {!isElectricity && (
                      <>
                        <div className="bill-item">
                          <span className="bill-item-label">{t('bills.rent')}</span>
                          <span className="bill-item-value">₹{bill.rent || 0}</span>
                        </div>
                        <div className="bill-item">
                          <span className="bill-item-label">{t('bills.water')}</span>
                          <span className="bill-item-value">₹{bill.water || 0}</span>
                        </div>
                      </>
                    )}
                    {(isElectricity || bill.electricity) && (
                      <div className="bill-item">
                        <span className="bill-item-label">{t('bills.electricity')}</span>
                        <span className="bill-item-value">₹{bill.electricity || 0}</span>
                      </div>
                    )}
                    {!isElectricity && (
                      <div className="bill-item">
                        <span className="bill-item-label">{t('bills.misc')}</span>
                        <span className="bill-item-value">₹{bill.miscellaneous || 0}</span>
                      </div>
                    )}
                  </div>
                  <div className="bill-total-row">
                    <span className="bill-total-label">{t('bills.total')}</span>
                    <span className="bill-total-value">₹{total}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default TenantBills;